import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Badge from '../UI/Badge';

export default function RecordList() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/records/my');
      setRecords(response.data.records || []);
    } catch (err) {
      setError(err.response?.data?.error || '세특 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await api.delete(`/api/records/${recordId}`);
      fetchRecords();
    } catch (err) {
      alert(err.response?.data?.error || '삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="text-center py-8">
        <p className="text-danger-500 mb-4">{error}</p>
        <Button onClick={fetchRecords}>다시 시도</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-neutral-800">생성한 세특</h2>
        <Button onClick={() => navigate('/records/generate')}>
          새 세특 생성
        </Button>
      </div>

      {records.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-neutral-500 mb-4">아직 생성한 세특이 없습니다.</p>
          <Button onClick={() => navigate('/records/generate')}>
            첫 세특 생성하기
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {records.map((record) => (
            <Card key={record.id} className="animate-fade-in">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-bold text-neutral-800">
                      {record.student?.username || '알 수 없음'}
                    </h3>
                    {record.isApproved && (
                      <Badge variant="success">승인됨</Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-neutral-600 mb-3">
                    {record.semester && <span>학기: {record.semester}</span>}
                    {record.subject && <span>과목: {record.subject}</span>}
                    {record.template && <span>템플릿: {record.template.title}</span>}
                  </div>

                  <div className="p-4 bg-neutral-50 rounded-xl">
                    <p className="text-neutral-700 line-clamp-3 whitespace-pre-wrap">
                      {record.content}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                <span className="text-sm text-neutral-500">
                  {new Date(record.createdAt).toLocaleDateString('ko-KR')}
                </span>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(record.content);
                      alert('클립보드에 복사되었습니다.');
                    }}
                  >
                    복사
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/api/records/${record.id}/export/text`, '_blank')}
                  >
                    내보내기
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(record.id)}
                  >
                    삭제
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
